import { useEffect, useState } from 'react'
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Clock,
  FileText,
  MapPin,
  AlertTriangle,
  Scale,
  XCircle,
  CreditCard,
  Banknote,
  CheckCircle2,
} from 'lucide-react'
import { api, formatMoney, formatQuantity, generateUUID, type OrderDetail as Order } from '../api/client'
import { StatusBadge } from './Orders'
import { OrderSuccessModal } from '../components/OrderSuccessModal'
import { useTranslation } from '../i18n/LanguageContext'

const ORDER_STEPS = [
  { key: 'PLACED', labelEn: 'Received', labelHi: 'प्राप्त हुआ' },
  { key: 'CONFIRMED', labelEn: 'Confirmed', labelHi: 'कन्फर्म हुआ' },
  { key: 'PACKING', labelEn: 'Packing', labelHi: 'पैकिंग जारी' },
  { key: 'OUT_FOR_DELIVERY', labelEn: 'Out for Delivery', labelHi: 'डिलीवरी के लिए निकला' },
  { key: 'DELIVERED', labelEn: 'Delivered', labelHi: 'डिलीवर हुआ' },
]

export function OrderDetail() {
  const { orderId } = useParams<{ orderId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { t, isHindi } = useTranslation()
  const justPlaced = Boolean((location.state as { justPlaced?: boolean } | null)?.justPlaced)
  const [order, setOrder] = useState<Order | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [reason, setReason] = useState('')
  const [payingOnline, setPayingOnline] = useState(false)
  const [switchingPayment, setSwitchingPayment] = useState(false)
  const [paymentNotice, setPaymentNotice] = useState<string | null>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [celebrationOrder, setCelebrationOrder] = useState<Order | null>(null)

  // Close success modal if hardware back button is pressed
  useEffect(() => {
    function handleAppBack(e: Event) {
      if (showSuccessModal) {
        e.preventDefault()
        setShowSuccessModal(false)
      }
    }
    window.addEventListener('app:backpress', handleAppBack)
    return () => window.removeEventListener('app:backpress', handleAppBack)
  }, [showSuccessModal])

  useEffect(() => {
    if (!orderId) return

    api.orders
      .find(orderId)
      .then(setOrder)
      .catch((failure: Error) => setError(failure.message))
  }, [orderId])

  async function cancel() {
    if (!orderId) return
    setBusy(true)

    try {
      setOrder(await api.orders.cancel(orderId, reason.trim() || undefined))
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'That order could not be cancelled.')
    } finally {
      setBusy(false)
    }
  }

  async function handleSwitchPaymentMethod(newMethod: 'ONLINE' | 'CASH_ON_DELIVERY') {
    if (!orderId) return
    setSwitchingPayment(true)
    setError(null)
    setPaymentNotice(null)
    try {
      const updated = await api.orders.updatePaymentMethod(orderId, newMethod)
      setOrder(updated)
      if (newMethod === 'CASH_ON_DELIVERY') {
        setPaymentNotice('Switched to Cash on Delivery. You can pay when order is delivered.')
      } else {
        setPaymentNotice('Switched to Online Payment.')
      }
      return updated
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Could not change payment method.')
      return null
    } finally {
      setSwitchingPayment(false)
    }
  }

  async function handleOnlinePay() {
    if (!orderId || !order) return
    setPayingOnline(true)
    setError(null)
    setPaymentNotice(null)
    try {
      let currentOrder = order
      if (currentOrder.paymentMethod !== 'ONLINE') {
        const updated = await api.orders.updatePaymentMethod(orderId, 'ONLINE')
        setOrder(updated)
        currentOrder = updated
      }

      const initiation = await api.payments.initiate(orderId, generateUUID())
      const keyId = initiation.keyId || import.meta.env.VITE_RAZORPAY_KEY_ID || 'rzp_test_TiXJoQw5fFV0JK'

      if (initiation.gatewayConfigured && initiation.gatewayOrderId && window.Razorpay) {
        const rzp = new window.Razorpay({
          key: keyId,
          amount: initiation.amountPaise,
          currency: initiation.currency || 'INR',
          name: 'RojanaMart',
          description: `Payment for Order #${currentOrder.orderNumber}`,
          order_id: initiation.gatewayOrderId,
          prefill: {
            name: currentOrder.deliveryAddress?.recipientName || '',
            contact: currentOrder.deliveryAddress?.phoneNumber || '',
          },
          theme: {
            color: '#047857',
          },
          handler: async (response) => {
            try {
              await api.payments.confirm(
                response.razorpay_order_id,
                response.razorpay_payment_id,
                response.razorpay_signature,
              )
              const updated = await api.orders.find(orderId)
              setOrder(updated)
              setPaymentNotice('Online payment completed successfully!')
              setCelebrationOrder(updated)
              setShowSuccessModal(true)
            } catch (confirmErr) {
              console.error('Payment confirmation error:', confirmErr)
            }
          },
        })
        rzp.open()
      } else {
        setError('Payment gateway is not currently reachable.')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not initiate online payment.')
    } finally {
      setPayingOnline(false)
    }
  }

  if (error) {
    return (
      <div className="card mx-auto my-10 max-w-md p-6 text-center">
        <AlertTriangle className="mx-auto h-10 w-10 text-red-500 mb-2" />
        <h2 className="text-base font-bold text-stone-900">Order Information Error</h2>
        <p className="mt-1 text-xs text-stone-500">{error}</p>
        <Link to="/orders" className="btn-primary mt-4">
          Back to Your Orders
        </Link>
      </div>
    )
  }

  if (!order) {
    return (
      <div className="mx-auto max-w-4xl py-12 text-center">
        <div className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-4 border-emerald-600 border-t-transparent" />
        <p className="text-sm font-medium text-stone-500">Retrieving order details…</p>
      </div>
    )
  }

  const currentStepIndex = ORDER_STEPS.findIndex((s) => s.key === order.status)
  const isCancelled = order.status === 'CANCELLED'

  return (
    <article className="mx-auto max-w-4xl space-y-6">
      {/* Top Breadcrumb */}
      <div className="flex items-center justify-between text-xs">
        <Link
          to="/orders"
          className="inline-flex items-center gap-1.5 font-bold text-emerald-800 hover:text-emerald-900 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>{isHindi ? 'ऑर्डर्स सूची पर वापस जाएं' : 'Back to your orders'}</span>
        </Link>

        <a
          href={api.invoice.url(order.orderId)}
          target="_blank"
          rel="noreferrer"
          className="btn-secondary py-1.5 px-3 text-xs font-bold inline-flex items-center gap-1.5"
        >
          <FileText className="h-3.5 w-3.5 text-stone-600" />
          <span>{isHindi ? 'GST इनवॉइस डाउनलोड करें' : 'Download GST Invoice'}</span>
        </a>
      </div>

      {/* Just Placed Reassurance Banner */}
      {justPlaced && (
        <div className="rounded-3xl border border-emerald-300 bg-gradient-to-r from-emerald-50 via-teal-50/70 to-emerald-50 p-4 sm:p-5 shadow-sm">
          <div className="flex items-start sm:items-center gap-3.5">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-md shadow-emerald-700/20">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="badge bg-emerald-700 text-white font-black text-[11px] tracking-wide uppercase px-2 py-0.5">
                  {t('order_banner_badge')}
                </span>
                <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-800">
                  <span className="h-2 w-2 rounded-full bg-emerald-600 animate-pulse" />
                  {t('order_banner_packing')}
                </span>
              </div>
              <h2 className="font-heading text-base sm:text-lg font-black text-stone-900 mt-1">
                {t('order_banner_title')}
              </h2>
              <p className="text-xs text-stone-600 mt-0.5">
                {t('order_banner_desc')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header Card */}
      <header className="card p-5 sm:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-stone-100 pb-4">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="font-heading text-xl font-black text-stone-900 sm:text-2xl">
                {t('orders_order_number', { num: order.orderNumber })}
              </h1>
              <StatusBadge status={order.status} />
            </div>
            <p className="mt-1 text-xs text-stone-500">
              {isHindi ? 'ऑर्डर समय:' : 'Placed'}{' '}
              {new Date(order.placedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
              {order.window ? ` · ${order.fulfilmentType === 'PICKUP' ? t('checkout_store_pickup') : t('checkout_home_delivery')} (${order.window.label})` : ''}
            </p>
          </div>

          <div className="text-right">
            <span className="money text-2xl font-black text-stone-900">
              {formatMoney(order.totalPaise)}
            </span>
            <span className="block text-xs font-semibold text-emerald-700">
              {order.paymentStatus === 'PAID'
                ? isHindi ? '✓ भुगतान संपन्न' : '✓ Paid'
                : order.paymentMethod === 'ONLINE'
                  ? t('order_payment_pending')
                  : t('checkout_payment_cod')}
            </span>
          </div>
        </div>

        {/* Multi-step progress tracker */}
        {!isCancelled && (
          <div className="py-2">
            <div className="flex items-center justify-between relative">
              <div className="absolute top-1/2 left-0 right-0 h-1 bg-stone-100 -translate-y-1/2 z-0" />
              <div
                className="absolute top-1/2 left-0 h-1 bg-emerald-600 -translate-y-1/2 transition-all duration-500 z-0"
                style={{
                  width: `${Math.max(0, (currentStepIndex / (ORDER_STEPS.length - 1)) * 100)}%`,
                }}
              />

              {ORDER_STEPS.map((step, idx) => {
                const isPassed = currentStepIndex >= idx
                const isCurrent = currentStepIndex === idx

                return (
                  <div key={step.key} className="relative z-10 flex flex-col items-center">
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-bold transition-all ${
                        isCurrent
                          ? 'border-emerald-600 bg-emerald-700 text-white shadow-md shadow-emerald-700/30'
                          : isPassed
                            ? 'border-emerald-600 bg-emerald-100 text-emerald-800'
                            : 'border-stone-200 bg-white text-stone-400'
                      }`}
                    >
                      {isPassed ? '✓' : idx + 1}
                    </div>
                    <span
                      className={`mt-1.5 text-[11px] font-semibold text-center hidden sm:block ${
                        isCurrent ? 'text-emerald-900 font-bold' : isPassed ? 'text-stone-700' : 'text-stone-400'
                      }`}
                    >
                      {isHindi ? step.labelHi : step.labelEn}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {isCancelled && (
          <div className="flex items-center gap-2 rounded-2xl bg-stone-100 p-3.5 text-xs text-stone-700 font-medium">
            <XCircle className="h-4 w-4 text-red-500 shrink-0" />
            <span>{t('order_detail_status_cancelled')}{order.cancellationReason ? `: ${order.cancellationReason}` : ''}</span>
          </div>
        )}

        {order.totalsAdjusted && (
          <div className="flex items-start gap-2.5 rounded-2xl bg-amber-50 p-3.5 text-xs text-amber-900 font-medium">
            <Scale className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
            <div>
              {t('order_weighed_notice')}
            </div>
          </div>
        )}
      </header>

      {/* Main Order Content */}
      <div className="grid gap-6 md:grid-cols-[1fr,340px]">
        {/* Left Column: Line Items & Delivery Address */}
        <div className="space-y-4">
          <section className="card overflow-hidden">
            <div className="border-b border-stone-100 p-4 font-heading text-sm font-bold text-stone-900">
              {t('order_items_ordered', { count: order.lines.length })}
            </div>

            <div className="divide-y divide-stone-100">
              {order.lines.map((line) => (
                <div key={line.lineNumber} className="flex items-center justify-between p-4 text-xs">
                  <div>
                    <h3 className="font-bold text-stone-900 sm:text-sm">{line.name}</h3>
                    <p className="money mt-0.5 text-stone-500">
                      {line.quantityAdjusted ? (
                        <span className="text-amber-800 font-semibold">
                          {formatQuantity(line.orderedQuantity)} {isHindi ? 'ऑर्डर' : 'ordered'} → {formatQuantity(line.finalQuantity)} {isHindi ? 'पैक' : 'packed'}
                        </span>
                      ) : (
                        <span>
                          {formatQuantity(line.finalQuantity)} {line.unitCode} × {formatMoney(line.unitPricePaise)}
                        </span>
                      )}
                    </p>
                  </div>

                  <span className="money text-sm font-bold text-stone-900">
                    {formatMoney(line.netPaise)}
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* Delivery Address */}
          {order.deliveryAddress && (
            <section className="card p-5 space-y-2">
              <div className="flex items-center gap-2 font-heading text-sm font-bold text-stone-900">
                <MapPin className="h-4 w-4 text-emerald-700" />
                <span>{t('order_delivering_to')}</span>
              </div>
              <div className="text-xs text-stone-600 pl-6 space-y-0.5">
                <p className="font-bold text-stone-900">{order.deliveryAddress.recipientName}</p>
                <p>{order.deliveryAddress.formattedSingleLine}</p>
                <p className="money font-medium text-stone-500">
                  {isHindi ? 'संपर्क मोबाइल:' : 'Contact:'} {order.deliveryAddress.phoneNumber}
                </p>
              </div>
            </section>
          )}

          {/* Status Timeline */}
          <section className="card p-5 space-y-3">
            <h2 className="font-heading text-sm font-bold text-stone-900 flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-emerald-700" />
              <span>{t('order_tracking_timeline')}</span>
            </h2>

            <ol className="space-y-3 border-l-2 border-emerald-100 ml-2 pl-4 text-xs">
              {order.history.map((event, idx) => (
                <li key={`${event.toStatus}-${event.occurredAt}-${idx}`} className="relative">
                  <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-emerald-600 ring-4 ring-white" />
                  <div className="font-bold text-stone-900 flex items-center gap-2">
                    <StatusBadge status={event.toStatus} />
                    <span className="text-[11px] text-stone-400 font-normal">
                      {new Date(event.occurredAt).toLocaleString('en-IN', { timeStyle: 'short', dateStyle: 'medium' })}
                    </span>
                  </div>
                  {event.reason && <p className="mt-0.5 text-stone-500 text-[11px]">{event.reason}</p>}
                </li>
              ))}
            </ol>
          </section>
        </div>

        {/* Right Column: Invoice & Payment Breakdown */}
        <div className="space-y-4">
          {/* Payment Status Notice */}
          {paymentNotice && (
            <div className="flex items-center gap-2 rounded-xl bg-emerald-50 p-3 text-xs font-semibold text-emerald-800 border border-emerald-200">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
              <span>{paymentNotice}</span>
            </div>
          )}

          {/* Unpaid Order Action Card: Both Cash on Delivery and Online Payment */}
          {order.paymentStatus !== 'PAID' && !isCancelled && (
            <section className="card p-5 space-y-3.5 bg-white border border-stone-200 shadow-sm">
              <div className="flex items-center justify-between border-b border-stone-100 pb-2.5">
                <div className="flex items-center gap-2">
                  {order.paymentMethod === 'ONLINE' ? (
                    <CreditCard className="h-4 w-4 text-emerald-700" />
                  ) : (
                    <Banknote className="h-4 w-4 text-emerald-700" />
                  )}
                  <h2 className="font-heading text-xs font-bold text-stone-900">
                    {order.paymentMethod === 'ONLINE' ? t('order_payment_pending') : t('checkout_payment_cod')}
                  </h2>
                </div>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                    order.paymentMethod === 'ONLINE'
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-emerald-100 text-emerald-800'
                  }`}
                >
                  {order.paymentMethod === 'ONLINE' ? t('order_payment_due_badge') : t('order_pay_at_delivery_badge')}
                </span>
              </div>

              {order.paymentMethod === 'ONLINE' ? (
                <div className="space-y-3">
                  <p className="text-[11px] text-stone-600 leading-relaxed">
                    {isHindi
                      ? 'Razorpay (UPI, कार्ड, नेटबैंकिंग) से सुरक्षित ऑनलाइन भुगतान पूरा करें, या कैश ऑन डिलीवरी चुनें।'
                      : 'Complete your online payment securely via Razorpay (UPI, Cards, Netbanking), or switch to Cash on Delivery.'}
                  </p>

                  <button
                    type="button"
                    disabled={payingOnline || switchingPayment}
                    onClick={handleOnlinePay}
                    className="btn-primary w-full py-2.5 text-xs font-bold shadow-md shadow-emerald-700/20 flex items-center justify-center gap-2"
                  >
                    <CreditCard className="h-4 w-4" />
                    <span>
                      {payingOnline
                        ? t('order_opening_razorpay')
                        : t('order_pay_now_razorpay', { amount: formatMoney(order.totals.totalPaise) })}
                    </span>
                  </button>

                  <div className="relative flex items-center justify-center py-0.5">
                    <div className="w-full border-t border-stone-200" />
                    <span className="bg-white px-2 text-[10px] font-semibold text-stone-400 uppercase tracking-wider">
                      {isHindi ? 'या' : 'OR'}
                    </span>
                  </div>

                  <button
                    type="button"
                    disabled={payingOnline || switchingPayment}
                    onClick={() => void handleSwitchPaymentMethod('CASH_ON_DELIVERY')}
                    className="w-full rounded-xl border border-stone-300 bg-stone-50 py-2.5 px-3 text-xs font-bold text-stone-800 hover:bg-emerald-50 hover:border-emerald-600 hover:text-emerald-900 transition flex items-center justify-center gap-2 shadow-sm"
                  >
                    <Banknote className="h-4 w-4 text-emerald-700" />
                    <span>
                      {switchingPayment ? t('order_switching_cash') : t('order_pay_cash_instead')}
                    </span>
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-[11px] text-stone-600 leading-relaxed">
                    {isHindi ? (
                      <>
                        आपका ऑर्डर <strong>कैश ऑन डिलीवरी</strong> पर है। डिलीवरी पार्टनर पहुंचने पर कृपया{' '}
                        <strong className="text-emerald-800">{formatMoney(order.totals.totalPaise)}</strong> नकद तैयार रखें।
                      </>
                    ) : (
                      <>
                        Your order is set to <strong>Cash on Delivery</strong>. Please keep{' '}
                        <strong className="text-emerald-800">{formatMoney(order.totals.totalPaise)}</strong> ready in cash
                        when our partner arrives.
                      </>
                    )}
                  </p>

                  <div className="pt-2 border-t border-stone-100">
                    <button
                      type="button"
                      disabled={payingOnline || switchingPayment}
                      onClick={handleOnlinePay}
                      className="w-full rounded-xl border border-emerald-600/40 bg-emerald-50/60 py-2.5 px-3 text-xs font-bold text-emerald-800 hover:bg-emerald-100 transition flex items-center justify-center gap-2 shadow-sm"
                    >
                      <CreditCard className="h-4 w-4 text-emerald-700" />
                      <span>{payingOnline ? t('order_opening_razorpay') : t('order_prefer_cashless')}</span>
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Paid Order Badge */}
          {order.paymentStatus === 'PAID' && (
            <div className="flex items-center gap-2 rounded-xl bg-emerald-50 p-3 text-xs font-semibold text-emerald-800 border border-emerald-200">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
              <span>
                {isHindi
                  ? `भुगतान पूरा हुआ (${order.paymentMethod === 'ONLINE' ? 'Razorpay ऑनलाइन' : 'नकद प्राप्त'})`
                  : `Payment Completed (${order.paymentMethod === 'ONLINE' ? 'Online via Razorpay' : 'Cash Collected'})`}
              </span>
            </div>
          )}

          <section className="card p-5 space-y-3 text-xs">
            <h2 className="font-heading text-sm font-bold text-stone-900">{t('order_payment_breakdown')}</h2>

            <div className="space-y-2 text-stone-600">
              <div className="flex justify-between">
                <span>{t('order_goods_subtotal')}</span>
                <span className="money font-semibold text-stone-900">
                  {formatMoney(order.totals.goodsSubtotalPaise)}
                </span>
              </div>

              {order.totals.lineDiscountsPaise > 0 && (
                <div className="flex justify-between text-emerald-700 font-semibold">
                  <span>{t('order_prod_discounts')}</span>
                  <span className="money">−{formatMoney(order.totals.lineDiscountsPaise)}</span>
                </div>
              )}

              {order.totals.couponDiscountPaise > 0 && (
                <div className="flex justify-between text-emerald-700 font-semibold">
                  <span>{t('checkout_coupon')} {order.totals.appliedCouponCode}</span>
                  <span className="money">−{formatMoney(order.totals.couponDiscountPaise)}</span>
                </div>
              )}

              <div className="flex justify-between">
                <span>{t('order_delivery_fee')}</span>
                <span className="money font-semibold">
                  {order.totals.freeDelivery ? (isHindi ? 'मुफ़्त' : 'FREE') : formatMoney(order.totals.deliveryFeePaise)}
                </span>
              </div>

              <div className="flex justify-between text-stone-500 text-[11px]">
                <span>{t('order_gst_included')}</span>
                <span className="money">{formatMoney(order.totals.totalTaxPaise)}</span>
              </div>

              <div className="border-t border-stone-200 pt-2 flex items-baseline justify-between text-stone-900 font-bold">
                <span className="text-sm">{t('order_total_amount')}</span>
                <span className="money text-lg text-emerald-950">
                  {formatMoney(order.totals.totalPaise)}
                </span>
              </div>

              <div className="flex justify-between text-stone-500 text-[11px]">
                <span>{t('order_amount_paid')}</span>
                <span className="money">{formatMoney(order.amountPaidPaise)}</span>
              </div>
            </div>
          </section>

          {/* Cancellation section if eligible */}
          {order.cancellable && (
            <section className="card p-4 space-y-3">
              <h2 className="font-heading text-xs font-bold text-stone-900">{t('order_cancel_title')}</h2>
              <p className="text-[11px] text-stone-500">
                {t('order_cancel_desc')}
              </p>
              <input
                type="text"
                placeholder={t('order_cancel_reason_ph')}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="field py-1.5 text-xs"
              />
              <button
                type="button"
                disabled={busy}
                onClick={() => void cancel()}
                className="btn-danger w-full py-2 text-xs font-bold"
              >
                {busy ? t('order_cancelling') : t('order_detail_cancel_btn')}
              </button>
            </section>
          )}
        </div>
      </div>

      {/* Online Payment Success Celebration Modal */}
      <OrderSuccessModal
        isOpen={showSuccessModal}
        order={celebrationOrder}
        onClose={() => setShowSuccessModal(false)}
        onTrackOrder={() => setShowSuccessModal(false)}
        onContinueShopping={() => navigate('/')}
      />
    </article>
  )
}
